import { CardConfig } from '../models/card-model';

/**
 * Server error when revision conflict occurs.
 */
export interface RevisionConflictError {
	type: 'REVISION_CONFLICT';
	message: string;
	currentRevisions: number;
	/**
	 * `false` when the server sent no `errorInfo.currentRevisions` and
	 * `currentRevisions` is only a guess (`revisions + 1`); retry must refetch.
	 */
	currentRevisionsKnown?: boolean;
	currentData: CardConfig;
}

/**
 * Persistence state tracked per card.
 */
export interface CardPersistenceState {
	cardId: string;

	/** Known server revision (null until first load/save) */
	serverRevisions: number | null;

	/** Pending change (debounced, not yet sent) */
	pendingChange: {
		patch: Partial<CardConfig>;
		timestamp: number;
	} | null;

	/** Changes being retried (offline or conflict recovery) */
	retryQueue: Array<{
		id: string;
		patch: Partial<CardConfig>;
		revisions: number;
		retryCount: number;
	}>;

	/** Current persistence status */
	status: 'idle' | 'saving' | 'conflict' | 'offline' | 'error';

	/** Auto-save debounce timer ID */
	debounceTimer: ReturnType<typeof setTimeout> | null;

	/** Max wait timer ID (forces save after MAX_DELAY_MS) */
	maxWaitTimer: ReturnType<typeof setTimeout> | null;

	/** Last error if status is 'error' or 'conflict' */
	lastError?: RevisionConflictError | Error;

	/** Last successful save timestamp */
	lastSavedAt?: string;

	/** Snapshot index (#vN) that live editor content currently represents */
	contentSnapshotVersion: number | null;

	/** Snapshot versions to restore on redo (LIFO) */
	redoTargets: number[];
}

/**
 * Options for persistence operations.
 */
export interface PersistenceOptions {
	/** Debounce window in ms (default: 500) */
	debounceMs: number;

	/** Maximum wait time in ms (default: 2000) */
	maxDelayMs: number;

	/** Maximum retry attempts for conflicts (default: 3) */
	maxRetries: number;

	/** Whether to show conflict resolution UI (default: true) */
	showConflictUI: boolean;
}

/**
 * Default persistence options.
 */
export const DEFAULT_PERSISTENCE_OPTIONS: PersistenceOptions = {
	debounceMs: 500,
	maxDelayMs: 2000,
	maxRetries: 3,
	showConflictUI: true,
};

/**
 * Event emitted when persistence state changes.
 */
export interface PersistenceStateChangeEvent {
	cardId: string;
	previousStatus: CardPersistenceState['status'];
	currentStatus: CardPersistenceState['status'];
	error?: RevisionConflictError | Error;
	revisions?: number;
}

/**
 * Event emitted when a change is successfully persisted.
 */
export interface ChangePersistedEvent {
	cardId: string;
	revisions: number;
	updatedAt: string;
	patch: Partial<CardConfig>;
}

/** Emitted when undo/redo availability changes for a card. */
export interface RestoreNavigationChangedEvent {
	cardId: string;
}
